<template>
    <div
        :class="[$style['mds-button-group'], classObject, sizeClass]"
        role="tablist"
        :data-mds-version="version"
        v-on:keyup.home="focusButton(0)"
        v-on:keyup.end="focusButton(focusableButtons.length - 1)"
        v-on:keydown.left="moveLeft"
        v-on:keydown.right="moveRight"
    >
        <mds-button-group-item
            v-for="(item, index) in content"
            :key="index"
            v-bind="item"
            v-on:mds-button-group-item-active="$emit('mds-button-group-item-active', $event)"
        >
        </mds-button-group-item>
    </div>
</template>

<script>
    import MdsButtonGroupItem from './button-group-item.vue';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsButtonGroup',
        components: {
            MdsButtonGroupItem,
        },
        data() {
            return {
                focusableButtons: null,
                version: `${name}-${version}`,
            };
        },
        props: {
            content: {
                type: Array,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets content for the button group, using the content schema defined below.',
                },
            },
            fullWidth: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, the button group stretches to the full with of its container.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the button group.',
                },
            },
        },
        provide() {
            // Properties that are available for injection into components descendants https://vuejs.org/v2/api/#provide-inject
            const parentVariation = {};
            Object.defineProperty(parentVariation, 'size', {
                enumerable: true,
                get: () => this.size,
            });
            return { parentVariation };
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-button-group--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-button-group--full-width']] = this.fullWidth === true;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
        },
        updated() {
            this.getFocusableButtons();
        },
        methods: {
            getCurrentIndex() {
                return [...this.focusableButtons].findIndex(button => button === document.activeElement);
            },
            getFocusableButtons() {
                this.focusableButtons = this.$el.querySelectorAll('button:not(:disabled)');
            },
            focusButton(index) {
                this.focusableButtons[index].focus();
            },
            moveLeft() {
                const currentIndex = this.getCurrentIndex();

                if (currentIndex === 0) {
                    this.focusButton(this.focusableButtons.length - 1);
                } else {
                    this.focusButton(currentIndex - 1);
                }
            },
            moveRight() {
                const currentIndex = this.getCurrentIndex();
                if (currentIndex === this.focusableButtons.length - 1) {
                    this.focusButton(0);
                } else {
                    this.focusButton(currentIndex + 1);
                }
            },
        },
        mounted() {
            this.getFocusableButtons();
        },
    };
</script>

<style lang="scss" module>
    @import './button-group.scss';
</style>
